/**
 * API functions for company order operations.
 * Matches backend endpoints from company_order_controller.py
 */

import { goSolidApi } from "@/composables/useGoSolidApi";
import type {
  AdminUpdateStatusRequest,
  AdminUpdateStatusResponse,
  CompanyOrderCancelResponse,
  CompanyOrderConfirmResponse,
  CompanyOrderDetailsResponse,
  CompanyOrderFiltersRequest,
  CompanyOrderListResponse,
  CompanyOrderUpdatePayload,
  CompanyOrderUpdateResponse,
} from "./companyOrderTypes";

export const companyOrderApi = {
  /**
   * Retrieve the companies' orders, optionally limited to `created_at` from
   * `date_from` (inclusive) to `date_before` (exclusive), both ISO 8601
   * instants. The backend reads only `company_ids` and the date range; the
   * other filter fields are applied client-side.
   *
   * Error Handling:
   * – Network/HTTP errors from `goSolidApi` are **not** caught here – they
   *   propagate to the caller, allowing components or composables to handle
   *   them via `try/catch` or the global error handler.
   * – Validation errors (e.g., missing `company_ids`) will result in a 422
   *   response from the backend and bubble up the same way.
   *
   * @param filters  Filter object containing at minimum `company_ids`.
   * @returns       Promise resolving to `CompanyOrderListResponse`.
   */
  listCompanyOrders: (filters: CompanyOrderFiltersRequest) => {
    return goSolidApi.get<CompanyOrderListResponse>("/api/company-orders", {
      params: {
        company_ids: filters.company_ids.join(","),
        date_from: filters.date_from ?? undefined,
        date_before: filters.date_before ?? undefined,
      },
    });
  },

  /**
   * Retrieve every company's non-completed orders for admin users, optionally
   * limited to `created_at` from `date_from` (inclusive) to `date_before`
   * (exclusive), both ISO 8601 instants. Errors propagate to the caller.
   */
  listAdminNonCompletedCompanyOrders: (
    dates: Pick<CompanyOrderFiltersRequest, "date_from" | "date_before">
  ) => {
    return goSolidApi.get<CompanyOrderListResponse>(
      "/api/admin/company-orders/non-completed",
      {
        params: {
          date_from: dates.date_from ?? undefined,
          date_before: dates.date_before ?? undefined,
        },
      }
    );
  },

  /**
   * Get detailed information for a specific company order.
   */
  getOrderDetails: (orderId: number) => {
    return goSolidApi.get<CompanyOrderDetailsResponse>(
      `/api/company-orders/${orderId}`
    );
  },

  /**
   * Edit a WAITING_CONFIRMATION order's portfolio items (and statements for CANCEL).
   */
  updateOrder: (orderId: number, payload: CompanyOrderUpdatePayload) => {
    return goSolidApi.put<CompanyOrderUpdateResponse>(
      `/api/company-orders/${orderId}`,
      payload
    );
  },

  /**
   * Confirm a company order (change status from WAITING_CONFIRMATION to PENDING).
   */
  confirmOrder: (orderId: number) => {
    return goSolidApi.post<CompanyOrderConfirmResponse>(
      `/api/company-orders/${orderId}/confirm`
    );
  },

  /**
   * Cancel a company order (change status to CANCELLED).
   * Only available for WAITING_CONFIRMATION orders.
   */
  cancelOrder: (orderId: number) => {
    return goSolidApi.post<CompanyOrderCancelResponse>(
      `/api/company-orders/${orderId}/cancel`
    );
  },

  // Admin functions
  /**
   * Admin function to update order status to COMPLETED, FAILED, or CANCELLED.
   * Requires super_admin role.
   */
  adminUpdateStatus: (
    orderId: number,
    status: "COMPLETED" | "FAILED" | "CANCELLED"
  ) => {
    const data: AdminUpdateStatusRequest = {
      status,
    };

    return goSolidApi.put<AdminUpdateStatusResponse>(
      `/api/admin/company-orders/${orderId}/status`,
      data
    );
  },

  /**
   * Admin function to retry a failed or pending order.
   * Requires super_admin role.
   */
  adminRetryOrder: (orderId: number) => {
    return goSolidApi.post<AdminUpdateStatusResponse>(
      `/api/admin/company-orders/${orderId}/retry`
    );
  },
};
